import React, {
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'
import styled, { keyframes } from 'styled-components'
import { Chiclet } from 'sutra/components/Chiclet/Chiclet'
import { accents, brand, gray } from 'sutra/tokens/colors'
import { motion } from 'sutra/tokens/motion'
import { radii } from 'sutra/tokens/radii'

import { useEscapeDismiss } from 'hooks/use-escape-dismiss'
import type { FocusEntityOption } from 'src/components/agent-forms/use-focus-entity-selection'

// The staff editor renders BenchmarkUniversePanel inside an antd <Modal>,
// whose default z-index is 1000 — this must clear it or the panel would
// open underneath the modal.
const FOCUS_PICKER_Z_INDEX = 1100

export interface FocusEntityPickerProps {
  /** Placeholder on the inner text input — the "+ add brand" affordance.
   * Stays visible even once chips are present (unlike sutra `MultiInput`,
   * where the placeholder is a content hint rather than an add affordance). */
  addLabel: string
  /** Selected values, rendered as removable chips inside the input. */
  values: string[]
  /** Subset of `values` the universe no longer offers — rendered in the
   * `warning` tone. Always empty in the create wizard. */
  staleValues: string[]
  options: FocusEntityOption[]
  isLoading: boolean
  onAdd: (value: string) => void
  onRemove: (value: string) => void
  /** Panel row shown when `options` is empty because every value has
   * already been added. Defaults to the same 'No matches' the
   * filter-miss case shows. */
  noOptionsLabel?: string
}

const Container = styled.div<{ $focused: boolean }>`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
  width: 100%;
  min-height: 30px;
  padding: 3px 6px;
  font-family: 'Poppins', system-ui, sans-serif;
  background: ${brand.white};
  border: 1px solid
    ${({ $focused }) => ($focused ? brand.stillNight[800] : gray[300])};
  border-radius: ${radii.sm}px;
  transition: border-color ${motion.duration.base}ms ${motion.ease};
`

const TextInput = styled.input`
  flex: 1;
  min-width: 110px;
  border: none;
  outline: none;
  font-family: inherit;
  font-size: 13px;
  color: ${brand.stillNight[800]};
  background: transparent;

  &::placeholder {
    color: #a4a7ae;
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(-4px); }
  to { opacity: 1; transform: translateY(0); }
`

const fadeOut = keyframes`
  from { opacity: 1; transform: translateY(0); }
  to { opacity: 0; transform: translateY(-4px); }
`

const Panel = styled.div<{
  $top: number
  $left: number
  $minWidth: number
  $closing: boolean
}>`
  position: fixed;
  top: ${({ $top }) => $top}px;
  left: ${({ $left }) => $left}px;
  width: max-content;
  min-width: ${({ $minWidth }) => $minWidth}px;
  z-index: ${FOCUS_PICKER_Z_INDEX};
  margin: 4px 0 0;
  padding: 4px;
  background: ${brand.white};
  border: 1px solid ${gray[200]};
  border-radius: ${radii.sm}px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  outline: none;
  animation: ${({ $closing }) => ($closing ? fadeOut : fadeIn)}
    ${motion.duration.fast}ms ${motion.ease} forwards;
`

const OptionsList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  max-height: 280px;
  overflow-y: auto;
`

const NoResults = styled.li`
  padding: 6px 10px;
  font-size: 13px;
  color: ${gray[400]};
`

const Option = styled.li<{ $highlighted: boolean }>`
  padding: 6px 10px;
  border-radius: ${radii.xs}px;
  font-size: 13px;
  user-select: none;
  color: ${brand.stillNight[800]};
  background: ${({ $highlighted }) =>
    $highlighted ? accents.reflectionBlue[50] : 'transparent'};
  cursor: pointer;
`

/**
 * Inline typeahead combobox for the Focus card's brand/product selection.
 * Selected values render as removable `Chiclet` chips inside one bordered
 * input, mirroring sutra `MultiInput`'s look — but chips are owned here as
 * props (`values`/`staleValues`/`onRemove`), not passed as `children`,
 * because the container also owns the focus ring, the click-to-focus
 * handler, and the anchor the portal panel measures; splitting chips into a
 * separate slot would split those concerns across two files. Kept local
 * to `agent-forms` rather than promoted into sutra: this is the only
 * consumer today, and sutra `MultiInput`'s own doc comment explicitly
 * disclaims dropdown/suggestion behavior ("that's `Select`'s job"). If a
 * second consumer needs this shape, promote it to
 * `sutra/components/MultiSelectInput/` then.
 *
 * Always shows a typeable input (not a button-triggered panel like sutra
 * `Select`) — type, see matches, Tab/Enter commits the highlighted match.
 * Values must come from `options` (the universe's known brands/products);
 * there is no free-text commit, so Tab/Enter with nothing highlighted is a
 * no-op. An empty `options` list (everything already added) no longer
 * disables the input — a box holding chips must stay interactive — instead
 * the panel shows `noOptionsLabel`. `isLoading` still disables the input:
 * the edit surfaces hydrate `values` before the option list arrives, and
 * those chips must stay removable during that window (removal doesn't
 * depend on `options`).
 *
 * Enter commits and keeps focus (so several values can be added back to
 * back without re-focusing); Tab commits too but does NOT preventDefault,
 * so focus advances to the next field the way Tab normally does. Backspace
 * with an empty draft pops the last chip.
 */
export const FocusEntityPicker: React.FC<FocusEntityPickerProps> = ({
  addLabel,
  values,
  staleValues,
  options,
  isLoading,
  onAdd,
  onRemove,
  noOptionsLabel,
}) => {
  const [query, setQuery] = useState('')
  const [isOpen, setIsOpen] = useState(false)
  const [isFocused, setIsFocused] = useState(false)
  const [highlightedIndex, setHighlightedIndex] = useState(0)

  // Drives the Panel's mount lifecycle so the exit animation has time to
  // play — mirrors sutra Select's identical `phase` derivation.
  const [phase, setPhase] = useState<'closed' | 'open' | 'closing'>('closed')
  const prevIsOpenRef = useRef(isOpen)
  if (isOpen !== prevIsOpenRef.current) {
    prevIsOpenRef.current = isOpen
    setPhase(isOpen ? 'open' : 'closing')
  }

  const filteredOptions = options.filter((option) =>
    option.label.toLowerCase().includes(query.toLowerCase()),
  )
  // A parent-driven `values` change while the panel is open can shrink
  // `filteredOptions` out from under a stale `highlightedIndex`, silently
  // turning Enter/Tab into a no-op — clamp defensively.
  const clampedHighlightedIndex = Math.min(
    highlightedIndex,
    Math.max(filteredOptions.length - 1, 0),
  )

  const [position, setPosition] = useState({ top: 0, left: 0, minWidth: 0 })
  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const listboxId = useId()

  useLayoutEffect(() => {
    if (!isOpen || !containerRef.current) return
    const rect = containerRef.current.getBoundingClientRect()
    const gap = 4
    setPosition({
      top: rect.bottom + gap,
      left: rect.left,
      minWidth: rect.width,
    })
    // `values` isn't read directly, but adding/removing a chip reflows the
    // container's height/width — re-measure so the panel doesn't drift.
  }, [isOpen, values])

  useEffect(() => {
    if (!isOpen) return

    const handleClickOutside = (event: MouseEvent): void => {
      const target = event.target as Node
      if (
        !containerRef.current?.contains(target) &&
        !panelRef.current?.contains(target)
      ) {
        setIsOpen(false)
      }
    }

    // Capture phase — see sutra Select's identical listener for why.
    document.addEventListener('mousedown', handleClickOutside, true)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside, true)
    }
  }, [isOpen])

  useEscapeDismiss({
    isOpen,
    onClose: () => setIsOpen(false),
  })

  const commit = (option: FocusEntityOption): void => {
    onAdd(option.value)
    setQuery('')
    setHighlightedIndex(0)
  }

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setQuery(event.target.value)
    setHighlightedIndex(0)
    setIsOpen(true)
  }

  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>,
  ): void => {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault()
        setIsOpen(true)
        setHighlightedIndex((prev) =>
          Math.min(prev + 1, filteredOptions.length - 1),
        )
        break
      case 'ArrowUp':
        event.preventDefault()
        setHighlightedIndex((prev) => Math.max(prev - 1, 0))
        break
      case 'Enter': {
        const option = filteredOptions[clampedHighlightedIndex]
        if (option) {
          event.preventDefault()
          commit(option)
        }
        break
      }
      case 'Tab': {
        // No preventDefault — Tab should still advance focus even as it
        // commits, unlike Enter which stays put for the next entry.
        const option = filteredOptions[clampedHighlightedIndex]
        if (option) commit(option)
        break
      }
      case 'Backspace': {
        const lastValue = query.length === 0 ? values.at(-1) : undefined
        if (lastValue) {
          onRemove(lastValue)
          setHighlightedIndex(0)
        }
        break
      }
    }
  }

  const handleContainerMouseDown = (
    event: React.MouseEvent<HTMLDivElement>,
  ): void => {
    // Only refocus when the padding itself was clicked — never steal the
    // click from a chip's remove button.
    if (event.target === event.currentTarget) {
      event.preventDefault()
      inputRef.current?.focus()
    }
  }

  return (
    <>
      <Container
        ref={containerRef}
        $focused={isFocused || isOpen}
        onMouseDown={handleContainerMouseDown}
      >
        {values.map((value) => (
          <Chiclet
            key={value}
            $tone={staleValues.includes(value) ? 'warning' : 'navy'}
            onRemove={() => onRemove(value)}
            removeLabel={`Remove ${value}`}
          >
            {value}
          </Chiclet>
        ))}
        <TextInput
          ref={inputRef}
          type="text"
          role="combobox"
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          aria-autocomplete="list"
          aria-controls={listboxId}
          aria-activedescendant={
            filteredOptions[clampedHighlightedIndex]
              ? `${listboxId}-${clampedHighlightedIndex}`
              : undefined
          }
          value={query}
          placeholder={addLabel}
          disabled={isLoading}
          onFocus={() => {
            setIsFocused(true)
            setIsOpen(true)
          }}
          onBlur={() => setIsFocused(false)}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
        />
      </Container>
      {phase !== 'closed' &&
        createPortal(
          <Panel
            ref={panelRef}
            $top={position.top}
            $left={position.left}
            $minWidth={position.minWidth}
            $closing={phase === 'closing'}
            onMouseDown={(event) => event.preventDefault()}
            onAnimationEnd={() => {
              if (phase === 'closing') setPhase('closed')
            }}
          >
            <OptionsList role="listbox" id={listboxId}>
              {filteredOptions.length === 0 && (
                <NoResults>
                  {options.length === 0
                    ? (noOptionsLabel ?? 'No matches')
                    : 'No matches'}
                </NoResults>
              )}
              {filteredOptions.map((option, index) => (
                <Option
                  key={option.value}
                  id={`${listboxId}-${index}`}
                  role="option"
                  aria-selected={false}
                  $highlighted={index === clampedHighlightedIndex}
                  onMouseEnter={() => setHighlightedIndex(index)}
                  onClick={() => commit(option)}
                >
                  {option.label}
                </Option>
              ))}
            </OptionsList>
          </Panel>,
          document.body,
        )}
    </>
  )
}

FocusEntityPicker.displayName = 'FocusEntityPicker'
